import { apiRequest } from "./baseApi";
import { parseJsonOrThrow } from "./responseUtils";

/**
 * GMA-1400 — vendor-agnostic IoT event history + audit export.
 *
 * Backs the client-profile "IoT Alert History" section: a durable view of a
 * client's alerts (acknowledged AND unacknowledged) with filters, plus a CSV
 * export for QA / SOP2 audit. Access is enforced server-side (GMA-710 ACL).
 */

/** Build a `?a=b&c=d` query string from defined, non-blank filter values. */
export const toIotEventQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      qs.append(key, value);
    }
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
};

/** A client's IoT events (acked + unacked) with optional filters + paging. */
export const fetchClientIotEvents = async (clientId, params = {}) => {
  const res = await apiRequest(
    `users/${clientId}/iot-events${toIotEventQuery(params)}`,
    { version: "v1" },
  );
  return parseJsonOrThrow(res, "IoT events");
};

/**
 * Export a client's IoT events as CSV for audit and trigger a browser download.
 * Uses the authenticated apiRequest (Bearer header), reads the CSV as a blob,
 * and saves it client-side. Throws with a readable message on non-2xx.
 */
export const exportClientIotEventsCsv = async (clientId, params = {}) => {
  const res = await apiRequest(
    `users/${clientId}/iot-events/export${toIotEventQuery({
      ...params,
      format: "csv",
    })}`,
    { version: "v1" },
  );

  if (!res.ok) {
    let message = `Export failed (status ${res.status})`;
    try {
      const body = await res.json();
      message = body?.message || body?.error || message;
    } catch (_ignored) {
      // non-JSON error body — keep the status message
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `iot-events-client-${clientId}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
